"use client";

import { useLocale } from "next-intl";
import * as React from "react";
import { NumericFormat } from "react-number-format";

import { currencyCodeFor } from "@/components/home/eligibility-currency";
import { useFundingEligibility } from "@/components/home/funding-eligibility-context";
import {
  buildFundingEligibilityIneligibleMessage,
  computeFundingEligibilityEstimate,
  formatFundingEligibilityRevenueDisplay,
  fundingEligibilityCurrencySymbol,
  getFundingEligibilityStatus,
  maxEnterableMonthlyRevenueFor,
  parseRevenue,
} from "@/components/home/funding-eligibility-shared";
import { SelectInput } from "@/components/ui/select-input";
import type { LiveHomeCopy } from "@/content/pages/home/get-live-home-copy";
import { cn } from "@/lib/utils";

const COMPACT_SELECT =
  "border-grey-100 h-10 rounded-lg border bg-white px-4 text-[12px] leading-[1.4] text-[#62666a] shadow-none hover:border-grey-100 data-popup-open:shadow-none [&>svg]:size-4 [&>svg]:text-[#62666a]";

/**
 * This card is shared by two callers with different step-one fields: the
 * homepage lets the user pick a real business category, while the restaurant
 * landing page collects time-in-business instead (the underwriting-multiplier
 * category is fixed to "Bars & Restaurants" there).
 */
const DEFAULT_ELIGIBILITY_CATEGORY = "Bars & Restaurants";

/** Trading history under 2 years is a hard eligibility cutoff (time-in-business mode only). */
const SHORT_TRADING_HISTORY_VALUES = new Set(["less-than-1", "1-year"]);

type FundingEligibilityCategoryCopy = LiveHomeCopy["fundingEligibility"];

/** Copy shape for the restaurant-page fork of the calculator — time-in-business, not category. */
export interface FundingEligibilityTimeInBusinessCopy {
  step01Title: string;
  step01Body: string;
  estimatedFundingLabel: string;
  estimatedFundingSuccessLabel: string;
  estimatedFundingUpToPrefix: string;
  ineligibleMessage: string;
  ineligibleTradingHistoryMessage: string;
  ineligibleRevenueLabel: string;
  estimatePrompt: string;
  congratsTitle: string;
  formMonthlyRevenueLabel: string;
  formMonthlyRevenueAria: string;
  formMonthlyRevenuePlaceholder: string;
  formTimeInBusinessLabel: string;
  formTimeInBusinessAria: string;
  formTimeInBusinessPlaceholder: string;
  formCountryLabel: string;
  formCountryAria: string;
  formCountryPlaceholder: string;
  timeInBusinessOptions: Array<{ value: string; label: string }>;
  countries: Array<{ value: string; label: string }>;
}

export type FundingEligibilityFormCopy =
  | FundingEligibilityCategoryCopy
  | FundingEligibilityTimeInBusinessCopy;

function isTimeInBusinessCopy(
  copy: FundingEligibilityFormCopy
): copy is FundingEligibilityTimeInBusinessCopy {
  return "timeInBusinessOptions" in copy;
}

interface FundingEligibilityCounterFormProps {
  copy: FundingEligibilityFormCopy;
  className?: string;
  onRangeChange?: (range: string) => void;
}

export function FundingEligibilityCounterForm({
  copy,
  className,
  onRangeChange,
}: FundingEligibilityCounterFormProps) {
  const locale = useLocale();
  const {
    formValues,
    setEligibilityState,
    setFormValues,
    markStepOneInteracted,
    reloadEstimate,
  } = useFundingEligibility();
  const { revenue, category, yearsInBusiness, country } = formValues;
  const timeInBusinessMode = isTimeInBusinessCopy(copy);

  const currency = fundingEligibilityCurrencySymbol(country, locale);
  const currencyCode = currencyCodeFor(country, locale);
  const isTradingHistoryTooShort =
    timeInBusinessMode && SHORT_TRADING_HISTORY_VALUES.has(yearsInBusiness);
  const effectiveCategory = timeInBusinessMode
    ? DEFAULT_ELIGIBILITY_CATEGORY
    : category;

  const status = React.useMemo(() => {
    if (timeInBusinessMode) {
      const isFormFilled =
        parseRevenue(revenue) !== null &&
        yearsInBusiness.trim().length > 0 &&
        country.trim().length > 0;
      if (!isFormFilled) return "incomplete" as const;
      if (isTradingHistoryTooShort) return "ineligible" as const;
    }
    return getFundingEligibilityStatus(revenue, effectiveCategory, country);
  }, [
    timeInBusinessMode,
    revenue,
    yearsInBusiness,
    country,
    isTradingHistoryTooShort,
    effectiveCategory,
  ]);
  const isEligible = status === "eligible";
  const revenueCeiling = maxEnterableMonthlyRevenueFor(currencyCode);
  const estimatedRange = React.useMemo(() => {
    if (!isEligible) return "";
    return (
      computeFundingEligibilityEstimate(
        revenue,
        currencyCode,
        locale,
        country,
        copy.estimatedFundingUpToPrefix,
        effectiveCategory
      ) ?? ""
    );
  }, [
    revenue,
    currencyCode,
    isEligible,
    locale,
    country,
    effectiveCategory,
    copy.estimatedFundingUpToPrefix,
  ]);
  const countryDisplayLabel = React.useMemo(
    () =>
      copy.countries.find((option) => option.value === country)?.label ??
      country,
    [copy.countries, country]
  );

  const ineligibleMessage = React.useMemo(() => {
    if (isTradingHistoryTooShort && timeInBusinessMode) {
      return copy.ineligibleTradingHistoryMessage;
    }
    if (status !== "ineligible") return null;
    return buildFundingEligibilityIneligibleMessage(
      copy.ineligibleMessage,
      revenue,
      currencyCode,
      locale,
      countryDisplayLabel
    );
  }, [
    isTradingHistoryTooShort,
    timeInBusinessMode,
    copy,
    status,
    revenue,
    currencyCode,
    locale,
    countryDisplayLabel,
  ]);
  const ineligibleRevenueDisplay = React.useMemo(() => {
    if (isTradingHistoryTooShort) return null;
    if (status !== "ineligible") return null;
    return formatFundingEligibilityRevenueDisplay(
      revenue,
      currencyCode,
      locale,
      country
    );
  }, [
    isTradingHistoryTooShort,
    status,
    revenue,
    currencyCode,
    locale,
    country,
  ]);

  React.useEffect(() => {
    setEligibilityState({
      status,
      ineligibleMessage,
      ineligibleRevenueDisplay,
    });
  }, [
    status,
    ineligibleMessage,
    ineligibleRevenueDisplay,
    setEligibilityState,
  ]);

  React.useEffect(() => {
    onRangeChange?.(estimatedRange);
  }, [estimatedRange, onRangeChange]);

  const changeTrackingValue = timeInBusinessMode ? yearsInBusiness : category;
  const previousChangeTrackingValueRef = React.useRef(changeTrackingValue);

  React.useEffect(() => {
    const previousValue = previousChangeTrackingValueRef.current;
    previousChangeTrackingValueRef.current = changeTrackingValue;

    if (
      previousValue !== changeTrackingValue &&
      previousValue.trim().length > 0 &&
      isEligible
    ) {
      reloadEstimate();
    }
  }, [changeTrackingValue, isEligible, reloadEstimate]);

  function handleRevenueChange(nextRevenue: string) {
    setFormValues({ ...formValues, revenue: nextRevenue });
  }

  /**
   * Pin an over-limit paste to the ceiling. `isAllowed` alone would drop the
   * paste and leave the field untouched; clamping in `onValueChange` can't
   * replace it, because a clamp that lands on the value already in state is a
   * no-op React never re-renders, letting the input's display drift past the
   * cap.
   */
  function handleRevenuePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const parsed = parseRevenue(event.clipboardData.getData("text"));
    if (parsed === null || parsed <= revenueCeiling) return;
    event.preventDefault();
    setFormValues({ ...formValues, revenue: String(revenueCeiling) });
  }

  function handleCategoryChange(nextCategory: string) {
    setFormValues({ ...formValues, category: nextCategory });
  }

  function handleYearsInBusinessChange(nextYearsInBusiness: string) {
    setFormValues({
      ...formValues,
      yearsInBusiness: nextYearsInBusiness,
      category: DEFAULT_ELIGIBILITY_CATEGORY,
    });
  }

  function handleCountryChange(nextCountry: string) {
    setFormValues({ ...formValues, country: nextCountry });
  }

  return (
    <div
      className={cn("flex flex-col gap-4", className)}
      onFocusCapture={markStepOneInteracted}
      onPointerDownCapture={markStepOneInteracted}
    >
      <FormField
        label={copy.formMonthlyRevenueLabel}
        htmlFor="funding-monthly-revenue"
      >
        <div className="border-grey-100 flex h-10 items-center gap-1 rounded-lg border bg-white px-4">
          <span className="text-[12px] leading-[1.4] text-[#62666a]">
            {currency}
          </span>
          <NumericFormat
            id="funding-monthly-revenue"
            inputMode="decimal"
            value={revenue}
            onValueChange={(values) => handleRevenueChange(values.value)}
            thousandSeparator=","
            decimalScale={0}
            allowNegative={false}
            placeholder={copy.formMonthlyRevenuePlaceholder}
            className="text-grey-950 min-w-0 flex-1 bg-transparent text-[12px] leading-[1.4] outline-none placeholder:text-[#b0b3b5]"
            aria-label={copy.formMonthlyRevenueAria}
            isAllowed={({ floatValue }) =>
              floatValue === undefined || floatValue <= revenueCeiling
            }
            onPaste={handleRevenuePaste}
          />
        </div>
      </FormField>

      {timeInBusinessMode ? (
        <FormField
          label={copy.formTimeInBusinessLabel}
          htmlFor="funding-time-in-business"
        >
          <SelectInput
            id="funding-time-in-business"
            value={yearsInBusiness}
            onChange={handleYearsInBusinessChange}
            optionItems={copy.timeInBusinessOptions}
            placeholder={copy.formTimeInBusinessPlaceholder}
            aria-label={copy.formTimeInBusinessAria}
            className={COMPACT_SELECT}
            modal={false}
          />
        </FormField>
      ) : (
        <FormField label={copy.formCategoryLabel} htmlFor="funding-category">
          <SelectInput
            id="funding-category"
            value={category}
            onChange={handleCategoryChange}
            optionGroups={copy.categoryGroups}
            placeholder={copy.formCategoryPlaceholder}
            aria-label={copy.formCategoryAria}
            className={COMPACT_SELECT}
            modal={false}
          />
        </FormField>
      )}

      <FormField label={copy.formCountryLabel} htmlFor="funding-country">
        <SelectInput
          id="funding-country"
          value={country}
          onChange={handleCountryChange}
          optionItems={copy.countries}
          placeholder={copy.formCountryPlaceholder}
          aria-label={copy.formCountryAria}
          className={COMPACT_SELECT}
          modal={false}
        />
      </FormField>
    </div>
  );
}

function FormField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={htmlFor}
        className="text-grey-600 text-[12px] leading-[1.4] tracking-[-0.24px]"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
